/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution,
    useFormMetadata,
    useFormsByIds
} from '@ngt/opms';

import { Chip, Skeleton, styled } from '@mui/material';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';

import { GridColDef } from '@mui/x-data-grid';






/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../api/dtos';
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));


const SCREEN_FAIL_REASON_LABELS: Partial<Record<Dtos.LookupScreenFailReasonType, string>> = {
    [Dtos.LookupScreenFailReasonType.ParticipantIneligible]: 'Participant ineligible for registration',
    [Dtos.LookupScreenFailReasonType.ClinicianDeemedUnsuitable]: 'Clinician/MDT deemed unsuitable',
    [Dtos.LookupScreenFailReasonType.ParticipantPrefersStandardCare]: 'Participant declined trial because they prefer standard of care',
    [Dtos.LookupScreenFailReasonType.ParticipantDeclinedNoReason]: 'Participant declined no reason given or not interested in the trial',
};

const getScreenFailReasonLabel = (reason?: number | null): string =>
    (reason != null && SCREEN_FAIL_REASON_LABELS[reason as Dtos.LookupScreenFailReasonType]) || 'Unknown';

const useFailedScreeningGridColDefs = (
    institutions: IInstitution[] | null,
    showInitials: boolean,
    showSite: boolean
): GridColDef<Dtos.Patient>[] => {

    const screeningFormMetadata = useFormMetadata(1); // screening form
    const { data: screeningForms, loading } = useFormsByIds<Dtos.ScreeningForm>(screeningFormMetadata);

    const reasonByPatientId = React.useMemo(() => {
        const map = new Map<number, number | undefined>();

        screeningForms?.forEach(form => {
            if (form.patientId == null) {
                return;
            }

            // there are multiple screening forms for a patient.
            if (form.screenFailReason != null || !map.has(form.patientId)) {
                map.set(form.patientId, form.screenFailReason);
            }
        });

        return map;
    }, [screeningForms]);

    const columns = React.useMemo(() => {
        const c: (GridColDef<Dtos.Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Temp. No',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials',
                //width: 200
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'screeningDate',
                headerName: 'Date Screened',
                renderCell: rowData => (
                    rowData.row.screeningDate ? DateTime.fromISO(rowData.row.screeningDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            },
            {
                field: 'screenFailReason',
                headerName: 'Reason',
                filterable: false,
                // The reason lives on the screening form, not the patient row
                valueGetter: (_value, row) => row.id != null ? reasonByPatientId.get(row.id) : undefined,
                renderCell: rowData => (
                    loading ? <Skeleton /> : getScreenFailReasonLabel(rowData.value)
                ),
                sortComparator: (a, b) => getScreenFailReasonLabel(a).localeCompare(getScreenFailReasonLabel(b)),
                flex: 1
            },
        ];

        return c.filter(column => !!column) as GridColDef<Dtos.Patient>[];
    }, [showSite, institutions, showInitials, reasonByPatientId, loading]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useFailedScreeningGridColDefs;